import { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import useRecaptcha from "../hooks/recaptcha";
import Button from "../components/Button";

const enquiryForm = {
  name: "",
  organisation: "",
  email: "",
  phone: "",
  message: "",
};

function validate(form) {
  const errors = {};

  if (!form.name.trim()) {
    errors.name = "Please enter a name.";
  }

  if (!form.organisation.trim()) {
    errors.organisation = "Please enter your organisation.";
  }

  if (!form.email.trim()) {
    errors.email = "Please enter your email.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Enter a valid email address.";
  }

  if (form.phone.trim() && !/^[\d\s()+-]{7,}$/.test(form.phone)) {
    errors.phone = "Enter a valid phone number.";
  }

  if (!form.message.trim()) {
    errors.message = "Please enter an enquiry.";
  } else if (form.message.trim().length < 10) {
    errors.message = "Message should be at least 10 characters.";
  }

  return errors;
}

export default function EnquiryForm() {
  const [form, setForm] = useState(enquiryForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const { captchaToken, setCaptchaToken, recaptchaRef, handleRecaptcha } =
    useRecaptcha();

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const validationErrors = validate(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0 || !captchaToken) {
      return;
    }

    try {
      setStatus("submitting");

      const res = await fetch("/api/enquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        throw new Error("Request failed");
      }

      setStatus("success");
      setForm(enquiryForm); //reset form
    } catch (err) {
      setStatus("error");
    }
  }

  const inputClasses = (field) =>
    `w-full rounded-lg border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 shadow-sm
     focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400
     ${errors[field] ? "border-red-400" : "border-slate-300"}`;

  return (
    <div className="flex min-h-[calc(100vh-80px)] items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        {status === "success" && (
          <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center">
            <h2 className="text-lg font-semibold text-emerald-900">
              Enquiry sent
            </h2>
            <p className="mt-1 text-sm text-emerald-700">
              Thank you for your enquiry, we'll get back to you shortly.
            </p>
          </div>
        )}

        <h1 className="text-xl font-semibold text-slate-900">
          Tell us what you're working on
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Fill in the form below, or contact us directly:
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-5">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-slate-700"
            >
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              placeholder="Jane Smith"
              className={`mt-1 ${inputClasses("name")}`}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="organisation"
              className="block text-sm font-medium text-slate-700"
            >
              Organisation
            </label>
            <input
              id="organisation"
              name="organisation"
              type="text"
              value={form.organisation}
              onChange={handleChange}
              placeholder="Your Organisation"
              className={`mt-1 ${inputClasses("organisation")}`}
            />
            {errors.organisation && (
              <p className="mt-1 text-xs text-red-600">{errors.organisation}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-slate-700"
            >
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="jane@example.com"
              className={`mt-1 ${inputClasses("email")}`}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-slate-700"
            >
              Phone{" "}
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              placeholder="+61 400 000 000"
              className={`mt-1 ${inputClasses("phone")}`}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-slate-700"
            >
              Details
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              placeholder="What are you working on?"
              className={`mt-1 ${inputClasses("message")}`}
            />
            {errors.message && (
              <p className="mt-1 text-xs text-red-600">{errors.message}</p>
            )}
          </div>

          {status === "error" && (
            <p className="text-sm text-red-600">
              Something went wrong sending your enquiry. Please contact your
              administrator.
            </p>
          )}

          <div className="mt-4 flex justify-center">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
              ref={recaptchaRef}
              onChange={handleRecaptcha}
            />
          </div>

          <Button
            type="submit"
            disabled={status === "submitting"}
            className="w-full rounded-lg bg-[#2563eb] px-4 py-2.5 text-sm font-medium text-white
                     hover:bg-[#2579a8] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "submitting" ? "Sending..." : "Send enquiry"}
          </Button>

          <div className="mt-6 space-y-1 text-center text-sm text-slate-500">
            <p>Ph: (08) 8345 5266</p>
            <p>113 Gilbert Street, Adelaide, SA 5000</p>
            <p>mccsa@mccsa.org.au</p>
          </div>
        </form>
      </div>
    </div>
  );
}
